import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { TrashIcon } from "../../../components/icons";
import PenPaperIcon from "../../../components/icons/PenPaperIcon";
import { tokens } from "../../../theme";
import AddSiteDialog from "./AddSiteDialog";
import {
  deleteSite,
  fetchSites,
  SiteListApiItem,
} from "./services/siteListingConfig";
import { Language } from "@mui/icons-material";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

export interface SiteItem {
  id: string;
  name: string;
  url: string;
  cover: string;
  category: string;
  status: "Live" | "Staging";
  lastUpdatedDate: string;
  lastUpdatedBy: string;
}

export const SitesListing: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission(PERMISSIONS.CONFIGURATION_ADD);
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const canDelete = hasPermission(PERMISSIONS.CONFIGURATION_DELETE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<SiteListApiItem | null>(null);
  const [sites, setSites] = useState<SiteListApiItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadSites = async () => {
    setLoading(true);
    try {
      const res = await fetchSites();
      setSites(res.data.results);
    } catch (err) {
      console.error("Failed to fetch sites", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  const handleEdit = (site: SiteListApiItem) => {
    setEditingSite(site);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSite(id);
      setSites((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Failed to delete site", err);
    }
  };

  const handleViewSite = (domain: string) => {
    window.open(`https://${domain}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: colors.primary[700],
            fontSize: "1.65rem",
          }}
        >
          Muzukuru Sites Listings
        </Typography>
        {canAdd && (
          <Button
            variant="contained"
            onClick={() => {
              setEditingSite(null);
              setDialogOpen(true);
            }}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              py: 1.2,
              fontWeight: 500,
            }}
          >
            Add Site
          </Button>
        )}
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: "24px",
          p: 3,
          backgroundColor: colors.primary[900],
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: colors.primary[400],
                    color: colors.primary[900],
                    fontWeight: 600,
                    borderTopLeftRadius: "12px",
                    borderBottomLeftRadius: "12px",
                    py: 2,
                    borderBottom: "none",
                  }}
                >
                  Website information
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: colors.primary[400],
                    color: colors.primary[900],
                    fontWeight: 600,
                    borderBottom: "none",
                  }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: colors.primary[400],
                    color: colors.primary[900],
                    fontWeight: 600,
                    borderBottom: "none",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: colors.primary[400],
                    color: colors.primary[900],
                    fontWeight: 600,
                    borderBottom: "none",
                  }}
                >
                  Last Updated
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: colors.primary[400],
                    color: colors.primary[900],
                    fontWeight: 600,
                    borderTopRightRadius: "12px",
                    borderBottomRightRadius: "12px",
                    borderBottom: "none",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 6, borderBottom: "none" }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: colors.primary[400] }}
                    />
                  </TableCell>
                </TableRow>
              ) : sites.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 8, borderBottom: "none" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: colors.primary[100],
                          fontWeight: 600,
                          fontSize: "1rem",
                        }}
                      >
                        No sites added yet
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                sites.map((site) => (
                  <TableRow
                    key={site.id}
                    sx={{ "& td": { borderBottom: "none", py: 2.5 } }}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          src={site.logo ?? undefined}
                          variant="rounded"
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "8px",
                            backgroundColor: colors.primary[800],
                            color: colors.primary[400],
                          }}
                        >
                          {!site.logo && <Language sx={{ fontSize: 24 }} />}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: colors.primary[700],
                              fontSize: "0.95rem",
                            }}
                          >
                            {site.name}
                          </Typography>
                          <Typography
                            sx={{
                              color: colors.primary[100],
                              fontSize: "0.8rem",
                            }}
                          >
                            {site.domain}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          color: colors.primary[700],
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        {site.category_display}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          color: colors.primary[700],
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        {site.status_display}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography
                          sx={{
                            color: colors.primary[700],
                            fontWeight: 500,
                            fontSize: "0.85rem",
                          }}
                        >
                          {new Date(site.updated_at).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            },
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            color: colors.primary[100],
                            fontSize: "0.75rem",
                          }}
                        >
                          by {site.last_updated_by_name ?? "—"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {canEdit && (
                          <IconButton
                            onClick={() => handleEdit(site)}
                            sx={{
                              color: colors.primary[400],
                              "&:hover": { color: colors.primary[500] },
                            }}
                          >
                            <PenPaperIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        )}

                        {canDelete && (
                          <IconButton
                            onClick={() => handleDelete(site.id)}
                            sx={{
                              color: "#E57373",
                              "&:hover": { color: "#D32F2F" },
                            }}
                          >
                            <TrashIcon />
                          </IconButton>
                        )}

                        <Button
                          variant="outlined"
                          onClick={() => handleViewSite(site.domain)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "20px",
                            color: colors.primary[400],
                            px: 2.5,
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            ml: 1,
                            "&:hover": {
                              borderColor: colors.primary[400],
                              backgroundColor: "rgba(109, 76, 65, 0.04)",
                            },
                          }}
                        >
                          View site
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <AddSiteDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={() => {
          setDialogOpen(false);
          loadSites();
        }}
        site={editingSite}
      />
    </Box>
  );
};
